import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useStyletron } from "baseui";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { ObjectViewRenderer } from "components/object-view-renderer";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Pencil, Trash } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";

import { Article } from "../articles";
import { ARTICLES_FIELDS } from "../articles.form";
import { ARTICLES_DELETE, ARTICLES_SHOW } from "../articles.gql";

export default function ArticlesShow(): React.ReactElement {
  const [, theme] = useStyletron();
  const { enqueueSnackbar } = useSnackbar();
  const { checkPermission } = useAuth();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { setIsFetching, setIsLoading } = useLoading();

  const { refetch, data, error } = useQuery(ARTICLES_SHOW, {
    variables: { id: id ? parseInt(id) : null },
    notifyOnNetworkStatusChange: true,
  });
  const article: Article = data?.article;

  useEffect(() => {
    if (error?.graphQLErrors)
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [error]);

  useEffect(() => {
    if (data?.article) refetch();
    setIsFetching(true);
  }, []);

  useEffect(() => data?.article && setIsFetching(false), [data]);

  const [deleteArticle] = useMutation(ARTICLES_DELETE);

  const submit = async () => {
    setIsLoading(true);

    try {
      await deleteArticle({
        variables: {
          articleDeleteInput: { id: id ? parseInt(id) : null },
        },
      });

      enqueueSnackbar({
        message: "Usunięto pomyślnie",
        variant: "success",
      });

      setIsConfirmDialogOpen(false);

      history.push("/articles");
    } catch (error: unknown) {
      setIsConfirmDialogOpen(false);

      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.article.read))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title={article?.name}
        labels={["Artykuły", "Wyświetlanie"]}
        goBackOption
        buttons={[
          {
            label: "Edytuj",
            kind: KIND.secondary,
            startEnhancer: <Pencil size={18} />,
            permission: checkPermission(PERMISSIONS.article.update),
            onClick: () => history.push(`/articles/${id}/update`),
          },
        ]}
        actions={[
          {
            label: "Usuń artykuł",
            icon: Trash,
            color: theme.colors.negative,
            onClick: () => setIsConfirmDialogOpen(true),
            permission: checkPermission(PERMISSIONS.article.delete),
          },
        ]}
      />
      <Content>
        <ObjectViewRenderer fields={ARTICLES_FIELDS} data={article} />

        <ConfirmDialog
          isOpen={isConfirmDialogOpen}
          label={`Usunięcie artykułu ${article?.name}`}
          close={() => setIsConfirmDialogOpen(false)}
          confirm={submit}
        />
      </Content>
    </article>
  );
}
